import { CalendarDays, Clock3, Home, Menu, Settings, TimerReset, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { useToast } from "../context/ToastContext.jsx";
import { getSettings, getUserRoutineEditMasterData, listenTodayLogs, listenTodayRoutines } from "../firebase/firestore.js";
import { getDayName, getTodayKey } from "../utils/date.js";
import { clearSectionFilter, getSectionFilter, saveSectionFilter, sectionKeyFromRecord } from "../utils/userPreferences.js";

const userNav = [
  { to: "/user/home", label: "Home", icon: Home },
  { to: "/user/routine-board", label: "Routine", icon: CalendarDays },
  { to: "/user/pending", label: "Pending OUT", icon: TimerReset }
];

export default function UserLayout() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [routines, setRoutines] = useState([]);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [routineMasters, setRoutineMasters] = useState({ teachers: [], subjects: [] });
  const [menuOpen, setMenuOpen] = useState(false);
  const [sectionFilter, setSectionFilterState] = useState(() => getSectionFilter(user?.uid || user?.id));
  const [loading, setLoading] = useState(true);
  const date = getTodayKey();
  const day = getDayName();

  function updateSectionFilter(nextFilter) {
    const clean = [...new Set((nextFilter || []).filter(Boolean))];
    if (clean.length) {
      saveSectionFilter(user?.uid || user?.id, clean);
    } else {
      clearSectionFilter(user?.uid || user?.id);
    }
    setSectionFilterState(clean);
  }

  useEffect(() => {
    let unsubscribeRoutines = null;
    let unsubscribeLogs = null;
    async function loadToday() {
      setLoading(true);
      try {
        const [appSettings, editMasters] = await Promise.all([getSettings(), getUserRoutineEditMasterData()]);
        setSettings(appSettings);
        setRoutineMasters(editMasters);
        unsubscribeRoutines = listenTodayRoutines(day, setRoutines);
        unsubscribeLogs = listenTodayLogs(date, setLogs);
      } catch (error) {
        showToast(error.message || "Unable to load today data", "error");
      } finally {
        setLoading(false);
      }
    }
    loadToday();
    return () => {
      unsubscribeRoutines?.();
      unsubscribeLogs?.();
    };
  }, [date, day, showToast]);

  const filteredRoutines = useMemo(() => {
    if (!sectionFilter.length) return routines;
    const allowed = new Set(sectionFilter);
    return routines.filter((routine) => allowed.has(sectionKeyFromRecord(routine)));
  }, [routines, sectionFilter]);

  const filteredRoutineIds = useMemo(() => new Set(filteredRoutines.map((routine) => routine.id || routine.routineId)), [filteredRoutines]);

  const filteredLogs = useMemo(() => {
    if (!sectionFilter.length) return logs;
    const allowed = new Set(sectionFilter);
    return logs.filter((log) => filteredRoutineIds.has(log.routineId) || allowed.has(sectionKeyFromRecord(log)));
  }, [logs, sectionFilter, filteredRoutineIds]);

  const pendingCount = useMemo(() => {
    return filteredLogs.filter((log) => log.actualIn && !log.actualOut && log.classStatus !== "suspended").length;
  }, [filteredLogs]);

  const todayContext = useMemo(
    () => ({
      date,
      day,
      routines: filteredRoutines,
      allRoutines: routines,
      logs: filteredLogs,
      allLogs: logs,
      settings,
      loading,
      pendingCount,
      sectionFilter,
      setSectionFilter: updateSectionFilter,
      routineMasters
    }),
    [date, day, filteredRoutines, routines, filteredLogs, logs, settings, loading, pendingCount, sectionFilter, routineMasters]
  );

  return (
    <div className="app-shell user-shell">
      <header className="mobile-topbar">
        <div>
          <p>{sectionFilter.length ? `${sectionFilter.length} section filter` : user?.role === "admin" ? "Staff View" : "Welcome"}</p>
          <h1>{user?.name || "PARIKSIT Staff"}</h1>
        </div>
        <div className="topbar-actions">
          {user?.role === "admin" ? (
            <Link className="btn btn-ghost view-switch-btn" to="/admin/dashboard">
              Admin Panel
            </Link>
          ) : null}
          <button className="icon-button" type="button" onClick={() => setMenuOpen(true)} aria-label="Open staff menu">
            <Menu size={21} />
          </button>
        </div>
      </header>
      {menuOpen ? (
        <div className="user-menu-layer" onClick={() => setMenuOpen(false)}>
          <aside className="user-menu-panel" onClick={(event) => event.stopPropagation()}>
            <div className="section-heading">
              <div>
                <p className="eyebrow">Menu</p>
                <h3>Staff Tools</h3>
              </div>
              <button className="icon-button" type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                <X size={19} />
              </button>
            </div>
            <Link className="user-menu-link" to="/user/settings" onClick={() => setMenuOpen(false)}>
              <Settings size={19} />
              <span>Section Settings</span>
            </Link>
            <Link className="user-menu-link" to="/user/routine-settings" onClick={() => setMenuOpen(false)}>
              <Clock3 size={19} />
              <span>Routine Settings</span>
            </Link>
          </aside>
        </div>
      ) : null}
      <main className="content-page user-content">
        <Outlet context={todayContext} />
      </main>
      <nav className="bottom-nav" aria-label="Normal user navigation">
        {userNav.map((item) => {
          const Icon = item.icon;
          const showBadge = item.to === "/user/pending" && pendingCount > 0;
          return (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? "active" : "")}>
              <span className="nav-icon-wrap">
                <Icon size={20} />
                {showBadge ? <span className="nav-badge">{pendingCount > 99 ? "99+" : pendingCount}</span> : null}
              </span>
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
